import { saveDefaultsToSource, type SaveDefaultsResult } from '../dev/saveDefaults';
import rigDefaults from './PodRacerRig.defaults.json';
import type { PodRacerAssemblyAssets, PodRacerRigTuning } from './Racer';

export const POD_RACER_RIG_DEFAULTS_FILE = 'src/game/PodRacerRig.defaults.json' as const;
export const DEFAULT_POD_RACER_RIG_TUNING_JSON = rigDefaults as PodRacerRigTuning;

export const POD_RACER_MODEL_ID = 'pod-assembly';
export const POD_RACER_FULL_TEXTURE_MODEL_ID = 'pod-assembly-full-textures';

export const POD_RACER_ASSEMBLY_ASSETS = {
  cockpitUrl: '/rig-optimized/red-spacecraft.glb',
  pilotUrl: '/rig-optimized/armored-suit.glb',
  engineLeftUrl: '/rig-optimized/left-engine.glb',
  engineRightUrl: '/rig-optimized/right-engine.glb',
} satisfies PodRacerAssemblyAssets;

export const POD_RACER_ASSEMBLY_FULL_TEXTURE_ASSETS = {
  cockpitUrl: '/rig/red-spacecraft.glb',
  pilotUrl: '/rig/armored-suit.glb',
  engineLeftUrl: '/rig/left-engine.glb',
  engineRightUrl: '/rig/right-engine.glb',
} satisfies PodRacerAssemblyAssets;

// Bumped to v4 / profiles:v2 with no legacy migration to orphan stale caches
// that were shadowing PodRacerRig.defaults.json on every boot. JSON values win
// on first boot; future Save-to-code calls write to the new keys.
const RIG_TUNING_STORAGE_KEY = 'twin-suns-turbo:pod-racer-rig-tuning:v4';
const RIG_TUNING_PROFILES_STORAGE_KEY = 'twin-suns-turbo:pod-racer-rig-tuning-profiles:v2';
const LEGACY_RIG_TUNING_STORAGE_KEYS: string[] = [];

type NumericRigTuningKey = {
  [K in keyof PodRacerRigTuning]: PodRacerRigTuning[K] extends number ? K : never;
}[keyof PodRacerRigTuning];

type BooleanRigTuningKey = {
  [K in keyof PodRacerRigTuning]: PodRacerRigTuning[K] extends boolean ? K : never;
}[keyof PodRacerRigTuning];

interface CachedRigTuningProfile {
  modelId: string;
  tuning: Partial<PodRacerRigTuning>;
  updatedAt: string;
}

export function loadCachedPodRacerRigTuning(
  modelId = POD_RACER_MODEL_ID,
): Partial<PodRacerRigTuning> {
  const profile = loadCachedRigTuningProfiles()[modelId];
  if (profile) return profile.tuning;

  const keys = [
    rigTuningModelStorageKey(modelId),
    ...(modelId === POD_RACER_MODEL_ID ? [RIG_TUNING_STORAGE_KEY, ...LEGACY_RIG_TUNING_STORAGE_KEYS] : []),
  ];
  for (const key of keys) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const tuning = sanitizeRigTuningPayload(JSON.parse(raw));
      if (Object.keys(tuning).length > 0) {
        saveCachedPodRacerRigTuning(modelId, tuning);
      }
      return tuning;
    } catch {
      continue;
    }
  }
  return {};
}

/**
 * Persist the live rig tuning to `PodRacerRig.defaults.json` via the dev-only
 * Vite middleware. Returns ok:false outside dev mode. Reload after save = the
 * new defaults ship with the code, no localStorage needed for a teammate.
 */
export function savePodRacerRigDefaultsToSource(
  tuning: PodRacerRigTuning,
): Promise<SaveDefaultsResult> {
  return saveDefaultsToSource(POD_RACER_RIG_DEFAULTS_FILE, tuning);
}

/** Wipe any cached rig tuning for every model so reload picks up source defaults. */
export function clearCachedPodRacerRigTuning(): void {
  try {
    window.localStorage.removeItem(RIG_TUNING_PROFILES_STORAGE_KEY);
    window.localStorage.removeItem(RIG_TUNING_STORAGE_KEY);
    for (const key of LEGACY_RIG_TUNING_STORAGE_KEYS) window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function saveCachedPodRacerRigTuning(
  modelId: string,
  tuning: Partial<PodRacerRigTuning>,
): void {
  try {
    const safeTuning = sanitizeRigTuning(tuning);
    const profile: CachedRigTuningProfile = {
      modelId,
      tuning: safeTuning,
      updatedAt: new Date().toISOString(),
    };
    const profiles = loadCachedRigTuningProfiles();
    profiles[modelId] = profile;
    window.localStorage.setItem(RIG_TUNING_PROFILES_STORAGE_KEY, JSON.stringify(profiles));
    window.localStorage.setItem(rigTuningModelStorageKey(modelId), JSON.stringify(profile));
    if (modelId === POD_RACER_MODEL_ID) {
      window.localStorage.setItem(RIG_TUNING_STORAGE_KEY, JSON.stringify(safeTuning));
    }
  } catch (err) {
    console.warn('[pod-racer] failed to cache rig tuning:', err);
  }
}

function rigTuningModelStorageKey(modelId: string): string {
  return `${RIG_TUNING_STORAGE_KEY}:${modelId}`;
}

function loadCachedRigTuningProfiles(): Record<string, CachedRigTuningProfile> {
  try {
    const raw = window.localStorage.getItem(RIG_TUNING_PROFILES_STORAGE_KEY);
    if (!raw) return {};
    const source = JSON.parse(raw) as Record<string, unknown>;
    const profiles: Record<string, CachedRigTuningProfile> = {};
    for (const [modelId, value] of Object.entries(source)) {
      const sourceProfile = value as Record<string, unknown>;
      const tuning = sanitizeRigTuningPayload(value);
      if (Object.keys(tuning).length === 0) continue;
      profiles[modelId] = {
        modelId,
        tuning,
        updatedAt: typeof sourceProfile?.updatedAt === 'string' ? sourceProfile.updatedAt : '',
      };
    }
    return profiles;
  } catch {
    return {};
  }
}

function sanitizeRigTuningPayload(value: unknown): Partial<PodRacerRigTuning> {
  if (!value || typeof value !== 'object') return {};
  const source = value as Record<string, unknown>;
  return sanitizeRigTuning('tuning' in source ? source.tuning : value);
}

function sanitizeRigTuning(value: unknown): Partial<PodRacerRigTuning> {
  if (!value || typeof value !== 'object') return {};
  const source = value as Record<string, unknown>;
  const safe: Partial<PodRacerRigTuning> = {};
  const numericKeys: NumericRigTuningKey[] = [
    'engineScale',
    'engineSpread',
    'engineHeight',
    'engineForward',
    'pilotScale',
    'pilotY',
    'pilotZ',
    'cableSag',
    'cockpitSocketX',
    'cockpitSocketY',
    'cockpitSocketZ',
    'engineSocketX',
    'engineSocketY',
    'engineSocketZ',
    'socketLaneGap',
    'socketLaneRise',
    'connectorScale',
    'cableRadius',
    'cableInsert',
    'cockpitSocketDepth',
    'engineSocketDepth',
    'cockpitSocketYawDeg',
    'cockpitSocketPitchDeg',
    'engineSocketYawDeg',
    'engineSocketPitchDeg',
    'engineNozzleY',
    'engineNozzleZ',
    'engineNozzleScale',
    'engineGlowHue',
    'engineGlowIntensity',
    'engineGlowRange',
    'blueGlowHue',
    'blueGlowIntensity',
    'blueGlowRange',
    'enginePlumeLength',
    'enginePlumeRadius',
    'enginePlumeOpacity',
    'emitterOffsetX',
    'emitterOffsetY',
    'emitterOffsetZ',
    'emitterScale',
    'emitterIntensity',
    'idleIntensity',
    'coreHueDeg',
    'outerHueDeg',
    'plasmaIntensity',
    'plasmaHueDeg',
    'cableStiffness',
    'cableDamping',
    'strutStiffness',
    'strutDamping',
    'inertiaMass',
    'restStiffness',
    'engineDamping',
    'cockpitReactionRoll',
    'cockpitReactionPitch',
    'cockpitWhipStrength',
    'cockpitWhipStiffness',
    'cockpitWhipDamping',
    'cockpitWhipMaxOffset',
    'engineYawGain',
    'engineYawSmooth',
    'cableSlackBoost',
    'cableWeaveDamping',
  ];
  const booleanKeys: BooleanRigTuningKey[] = [
    'couplingEnabled',
    'inertialLagEnabled',
  ];
  for (const key of numericKeys) {
    const raw = source[key];
    if (typeof raw === 'number' && Number.isFinite(raw)) safe[key] = raw;
  }
  for (const key of booleanKeys) {
    const raw = source[key];
    if (typeof raw === 'boolean') safe[key] = raw;
  }
  return safe;
}
